import { useRef, useCallback } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { styled } from '@mui/material'
import { BulkSheetView } from '../../containers/BulkSheetView'
import { PageArea } from '..'
import { ApplicationFunctionPermissionRow } from './types'
import { usePermissionPolicyOptions } from '../../hooks/usePermissionPolicyOptions'
import { usePermissionPolicyData } from './hooks/usePermissionPolicyData'
import { usePermissionPolicyGridOptions } from './gridOptions'
import { useBulkSheetState } from '../../containers/BulkSheetView/hooks/bulkSheetState/bulkSheetState'
import { useColumnState } from '../../containers/BulkSheetView/hooks/columnState/useColumnState'
import { useColumnSetting } from '../../containers/BulkSheetView/components/columnSelector/useColumnSetting'
import ColumnSettingPopper from '../../containers/BulkSheetView/components/columnSelector/ColumnSettingPopper'
import ReloadButton from '../../containers/BulkSheetView/components/header/ReloadButton'
import FilterButton from '../../containers/BulkSheetView/components/header/FilterButton'
import SortButton from '../../containers/BulkSheetView/components/header/SortButton'
import ColumnSettingButton from '../../containers/BulkSheetView/components/header/ColumnSettingButton'
import { UiStateKey } from '../../../lib/commons/uiStates'
import _ from 'lodash'
import { PermissionPolicyOption } from '../../../domain/value-object/PermissionPolicy'

type Props = {
  uuid: string
}

const PermissionPolicies = ({ uuid: functionUuid }: Props) => {
  const {
    initialized: permissionPolicyOptionsInitialized,
    allPermissionPolicyOptions,
  } = usePermissionPolicyOptions()

  if (!permissionPolicyOptionsInitialized) {
    return <></>
  }

  return (
    <Page
      functionUuid={functionUuid}
      allPermissionPolicies={allPermissionPolicyOptions}
    />
  )
}

const Page = ({
  functionUuid,
  allPermissionPolicies,
}: {
  functionUuid: string
  allPermissionPolicies: PermissionPolicyOption[]
}) => {
  const gridRef = useRef<AgGridReact<ApplicationFunctionPermissionRow>>(null)
  const bulkSheetRef = useRef<HTMLDivElement>(null)

  const { rowData } = usePermissionPolicyData({
    permissionPolicyOptions: allPermissionPolicies,
  })

  const gridOptions = usePermissionPolicyGridOptions({
    permissionPolicyOptions: allPermissionPolicies,
  })

  const bulkSheetState = useBulkSheetState(
    functionUuid,
    UiStateKey.PermissionPolicyColumnAndFilterState
  )

  const columnState = useColumnState({ gridOptions, bulkSheetState })
  const columnSetting = useColumnSetting()

  const handleReload = useCallback(() => {
    gridOptions.api?.refreshCells({ force: true })
  }, [gridOptions])

  const onGridReady = useCallback(() => {
    if (!_.isEmpty(bulkSheetState.columnState) && gridOptions.columnApi) {
      gridOptions.columnApi.applyColumnState({
        state: bulkSheetState.columnState,
        applyOrder: true,
      })
    }
  }, [bulkSheetState.columnState, gridOptions.columnApi])

  const onFirstDataRendered = useCallback(() => {
    if (!gridOptions.api) return

    if (!_.isEmpty(bulkSheetState.filterState)) {
      gridOptions.api.setFilterModel(bulkSheetState.filterState)
    }
    bulkSheetState.finishRestoring()
  }, [
    bulkSheetState.filterState,
    bulkSheetState.finishRestoring,
    gridOptions.api,
  ])

  if (!bulkSheetState.initialized) {
    return <></>
  }
  return (
    <PageArea>
      <Header>
        <ReloadButton onReload={handleReload} />
        <FilterButton
          filteredColumns={columnState.filteredColumns}
          onDeleteFilteredColumn={columnState.onDeleteFilteredColumn}
          resetFilters={columnState.resetFilters}
        />
        <SortButton
          onDeleteSortedColumn={columnState.onDeleteSortedColumn}
          onDeleteSortedAllColumns={columnState.onDeleteSortedAllColumns}
          onChangeSortColumnState={columnState.onChangeSortColumnState}
          sortColumnsState={columnState.sortColumnsState}
        />
        <ColumnSettingButton
          onClick={columnSetting.toggle}
          open={columnSetting.isOpen}
        />
      </Header>
      <BulkSheetView
        ref={bulkSheetRef}
        gridRef={gridRef}
        gridOptions={gridOptions}
        rowData={rowData}
        // Events and actions
        onGridReady={onGridReady}
        onFirstDataRendered={onFirstDataRendered}
        onColumnVisible={columnState.onColumnVisible}
        onColumnResized={columnState.rememberColumnState}
        onColumnMoved={columnState.rememberColumnState}
        onFilterChanged={columnState.onFilterChanged}
        onSortChanged={columnState.onSortChanged}
      />
      <ColumnSettingPopper
        anchorEl={columnSetting.anchorEl}
        open={columnSetting.isOpen}
        close={columnSetting.close}
        columnApi={gridOptions.columnApi || undefined}
        gridApi={gridOptions.api || undefined}
        height={bulkSheetRef.current?.offsetHeight}
        openSavedUiStateDialog={undefined}
        initializeColumnState={columnState.resetColumnState}
        applicationFunctionUuid={functionUuid}
        uiStateKey={UiStateKey.BulkSheetUIStateColumnAndFilter}
        columnState={bulkSheetState.columnState}
        offset={120}
      />
    </PageArea>
  )
}

const Header = styled('div')({
  display: 'flex',
  alignItems: 'center',
  margin: '10px 10px 5px 10px',
  overflowX: 'auto',
  overflowY: 'hidden',
  minHeight: '47px',
})

export default PermissionPolicies
